import { useEffect } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  ClipboardList,
  Clock,
  Quote,
  Sparkles,
} from 'lucide-react'
import Header from '@/components/Header'
import Footer from '@/components/Footer'
import ChatbotPopup from '@/components/ChatbotPopup'
import { translations } from '@/lib/translations'
import {
  getRelatedServices,
  getServiceBySlug,
  getServiceCopy,
} from '@/lib/services'

function ServicePhoto({ src, alt, className, position = 'object-center' }) {
  return (
    <img
      src={src}
      alt={alt}
      className={`${className} ${position}`}
      referrerPolicy="no-referrer"
      loading="lazy"
      onError={(event) => {
        event.currentTarget.src = '/placeholder.jpg'
      }}
    />
  )
}

export default function ServiceDetailPage({ language, setLanguage, theme, setTheme }) {
  const t = translations[language]
  const ui = t.servicePage || {}
  const { slug } = useParams()
  const navigate = useNavigate()
  const service = getServiceBySlug(slug)
  const copy = service ? getServiceCopy(service, language) : null
  const related = getRelatedServices(slug)
  const lang = language === 'am' ? 'am' : 'en'
  const Icon = service?.icon

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [slug])

  useEffect(() => {
    const previous = document.title
    document.title = copy ? `${copy.title} | Fortino Tech` : 'Fortino Tech'
    return () => {
      document.title = previous
    }
  }, [copy])

  const goToContact = () => {
    navigate('/#contact')
  }

  return (
    <div className="bg-background text-foreground min-h-screen">
      <Header
        language={language}
        toggleLanguage={() => setLanguage(language === 'en' ? 'am' : 'en')}
        isDarkMode={theme === 'dark'}
        toggleDarkMode={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
      />

      {!service || !copy ? (
        <main className="pt-32 pb-20 px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-3xl font-bold mb-4">{ui.notFound || 'This service was not found.'}</h1>
            <Link to="/#services" className="text-primary inline-flex items-center gap-2">
              <ArrowLeft className="w-4 h-4" />
              {ui.back || 'Back to services'}
            </Link>
          </div>
        </main>
      ) : (
        <main>
          <section className="relative min-h-[72vh] flex items-end overflow-hidden">
            <ServicePhoto
              src={service.images.hero}
              alt={service.images.heroAlt[lang]}
              position={service.images.heroPosition}
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background via-background/80 to-black/45" />
            <div className="relative max-w-7xl mx-auto w-full px-4 pt-32 pb-14">
              <Link
                to="/#services"
                className="inline-flex items-center gap-2 text-sm text-white/80 hover:text-white mb-6"
              >
                <ArrowLeft className="w-4 h-4" />
                {ui.back || 'Back to services'}
              </Link>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/15 backdrop-blur-sm mb-5">
                {Icon && (
                  <span className={`w-8 h-8 rounded-lg bg-gradient-to-br ${service.iconGradient} flex items-center justify-center`}>
                    <Icon className="w-4 h-4 text-white" />
                  </span>
                )}
                <span className="text-sm font-medium text-white/90">{copy.title}</span>
              </div>
              <h1 className="text-4xl md:text-6xl font-bold max-w-4xl mb-5 leading-tight">
                {copy.title}
              </h1>
              {copy.duration && (
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/15 mb-5">
                  <Clock className="w-4 h-4 text-white" />
                  <span className="text-sm text-white/90">
                    {ui.duration || 'Typical duration'}: {copy.duration}
                  </span>
                </div>
              )}
              <p className="text-lg md:text-xl text-muted-foreground max-w-3xl leading-relaxed">
                {copy.tagline}
              </p>
              <button
                type="button"
                onClick={goToContact}
                className={`mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r ${service.accent} text-white font-medium hover:opacity-90 transition-opacity`}
              >
                {ui.ctaButton || 'Discuss this project'}
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </section>

          <section className="px-4 py-16 md:py-20">
            <div className="max-w-7xl mx-auto grid lg:grid-cols-[1.4fr_0.8fr] gap-10">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/5 border border-primary/10 mb-5">
                  <Sparkles className="w-4 h-4 text-primary" />
                  <span className="text-xs font-medium text-primary uppercase tracking-wide">
                    {ui.overview || 'Overview'}
                  </span>
                </div>
                <div className="space-y-5 text-muted-foreground leading-relaxed text-base md:text-lg">
                  {copy.overview.map((paragraph) => (
                    <p key={paragraph.slice(0, 32)}>{paragraph}</p>
                  ))}
                </div>
              </div>

              <aside className="space-y-6">
                <div className="rounded-2xl border border-border bg-card p-6">
                  <h2 className="text-lg font-semibold mb-4">{copy.audienceTitle}</h2>
                  <ul className="space-y-3">
                    {copy.audience.map((item) => (
                      <li key={item} className="flex gap-3 text-sm text-muted-foreground">
                        <CheckCircle2 className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="rounded-2xl border border-border bg-card p-6">
                  <h2 className="text-lg font-semibold mb-4">{copy.toolsTitle}</h2>
                  <div className="flex flex-wrap gap-2">
                    {copy.tools.map((tool) => (
                      <span
                        key={tool}
                        className="px-3 py-1.5 rounded-full text-xs bg-primary/5 border border-primary/10 text-foreground"
                      >
                        {tool}
                      </span>
                    ))}
                  </div>
                </div>
              </aside>
            </div>
          </section>

          {(copy.requirements.length > 0 || copy.timeline.length > 0) && (
            <section className="px-4 pb-16">
              <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-8">
                {copy.requirements.length > 0 && (
                  <div className="rounded-2xl border border-border bg-card p-6 md:p-8">
                    <div className="flex items-center gap-2 mb-5">
                      <ClipboardList className="w-5 h-5 text-primary" />
                      <h2 className="text-2xl font-bold">{copy.requirementsTitle || ui.requirements || 'What we need to implement'}</h2>
                    </div>
                    <ul className="space-y-3">
                      {copy.requirements.map((item) => (
                        <li key={item} className="flex gap-3 text-sm text-muted-foreground">
                          <CheckCircle2 className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {copy.timeline.length > 0 && (
                  <div className="rounded-2xl border border-border bg-card p-6 md:p-8">
                    <div className="flex items-center gap-2 mb-5">
                      <Clock className="w-5 h-5 text-primary" />
                      <h2 className="text-2xl font-bold">{copy.timelineTitle || ui.timeline || 'Typical timeline'}</h2>
                    </div>
                    <ol className="space-y-4">
                      {copy.timeline.map((item, index) => (
                        <li key={item.phase} className="flex gap-4">
                          <span className={`w-9 h-9 rounded-lg bg-gradient-to-br ${service.iconGradient} text-white flex items-center justify-center text-sm font-bold shrink-0`}>
                            {index + 1}
                          </span>
                          <div>
                            <div className="flex flex-wrap items-baseline gap-2 mb-1">
                              <h3 className="font-semibold">{item.phase}</h3>
                              <span className="text-xs text-primary font-medium">{item.duration}</span>
                            </div>
                            <p className="text-sm text-muted-foreground leading-relaxed">{item.detail}</p>
                          </div>
                        </li>
                      ))}
                    </ol>
                  </div>
                )}
              </div>
            </section>
          )}

          <section className="px-4 pb-16">
            <div className="max-w-7xl mx-auto">
              <h2 className="text-3xl font-bold mb-8">{copy.includedTitle}</h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {copy.included.map((item) => (
                  <div key={item} className="rounded-2xl border border-border bg-card p-5">
                    <CheckCircle2 className="w-5 h-5 text-primary mb-3" />
                    <p className="text-sm leading-relaxed">{item}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="px-4 pb-16">
            <div className="max-w-7xl mx-auto">
              <h2 className="text-3xl font-bold mb-8">{ui.gallery || 'Images from this service'}</h2>
              <div className="grid md:grid-cols-3 gap-5">
                {service.images.gallery.map((item) => (
                  <figure key={item.src} className="group rounded-2xl overflow-hidden border border-border bg-card">
                    <div className="h-56 overflow-hidden bg-muted">
                      <ServicePhoto
                        src={item.src}
                        alt={item.alt[lang]}
                        position={item.position}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <figcaption className="p-4 text-sm text-muted-foreground">
                      {item.caption[lang]}
                    </figcaption>
                  </figure>
                ))}
              </div>
            </div>
          </section>

          <section className="px-4 pb-16">
            <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-10">
              <div>
                <h2 className="text-3xl font-bold mb-8">{copy.processTitle}</h2>
                <ol className="space-y-4">
                  {copy.process.map((step, index) => (
                    <li key={step.title} className="flex gap-4 rounded-2xl border border-border bg-card p-5">
                      <span className={`w-10 h-10 rounded-xl bg-gradient-to-br ${service.iconGradient} text-white flex items-center justify-center font-bold shrink-0`}>
                        {index + 1}
                      </span>
                      <div>
                        <h3 className="font-semibold mb-1">{step.title}</h3>
                        <p className="text-sm text-muted-foreground leading-relaxed">{step.text}</p>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>
              <div>
                <h2 className="text-3xl font-bold mb-8">{copy.benefitsTitle}</h2>
                <ul className="space-y-4">
                  {copy.benefits.map((item) => (
                    <li key={item} className="rounded-2xl border border-border bg-card p-5 flex gap-3">
                      <CheckCircle2 className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                      <span className="leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </section>

          <section className="px-4 pb-16">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold mb-8">{copy.faqsTitle}</h2>
              <div className="space-y-4">
                {copy.faqs.map((item) => (
                  <div key={item.q} className="rounded-2xl border border-border bg-card p-6">
                    <h3 className="font-semibold mb-2 flex gap-2">
                      <Quote className="w-4 h-4 text-primary shrink-0 mt-1" />
                      {item.q}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed pl-6">{item.a}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="px-4 pb-16">
            <div className="max-w-7xl mx-auto">
              <h2 className="text-3xl font-bold mb-8">{ui.related || 'Other services'}</h2>
              <div className="grid md:grid-cols-3 gap-6">
                {related.map((item) => {
                  const relatedCopy = getServiceCopy(item, language)
                  const RelatedIcon = item.icon
                  return (
                    <Link
                      key={item.slug}
                      to={`/services/${item.slug}`}
                      className="group rounded-2xl overflow-hidden border border-border bg-card hover:border-primary/40 transition-all"
                    >
                      <div className="h-40 overflow-hidden bg-muted">
                        <ServicePhoto
                          src={item.images.hero}
                          alt={item.images.heroAlt[lang]}
                          position={item.images.heroPosition}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-5">
                        <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${item.iconGradient} flex items-center justify-center mb-3`}>
                          <RelatedIcon className="w-5 h-5 text-white" />
                        </div>
                        <h3 className="font-semibold mb-2">{relatedCopy.title}</h3>
                        {relatedCopy.duration && (
                          <p className="text-xs text-primary mb-2">{relatedCopy.duration}</p>
                        )}
                        <p className="text-sm text-muted-foreground line-clamp-2 mb-3">{relatedCopy.shortDesc}</p>
                        <span className="inline-flex items-center gap-2 text-primary text-sm font-medium">
                          {ui.learnMore || 'View details'}
                          <ArrowRight className="w-4 h-4" />
                        </span>
                      </div>
                    </Link>
                  )
                })}
              </div>
            </div>
          </section>

          <section className="px-4 pb-20">
            <div className={`max-w-7xl mx-auto rounded-3xl overflow-hidden bg-gradient-to-r ${service.accent} p-8 md:p-12 text-white`}>
              <h2 className="text-3xl md:text-4xl font-bold mb-3">
                {ui.ctaTitle || 'Ready to start this project?'}
              </h2>
              <p className="text-white/85 max-w-2xl mb-8">
                {ui.ctaText || 'Tell us what you need. We will reply with a clear plan and a realistic timeline.'}
              </p>
              <button
                type="button"
                onClick={goToContact}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white text-slate-900 font-medium hover:bg-white/90"
              >
                {ui.ctaButton || 'Discuss this project'}
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </section>
        </main>
      )}

      <Footer language={language} />
      <ChatbotPopup language={language} />
    </div>
  )
}
